import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Play,
  Lock,
  Crown,
  Target,
  BookOpen,
  ArrowLeft,
  FlaskConical,
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Advanced Lab covers L11–L17 — rare, "quirky" techniques that don't
// appear in NYT-style puzzles (Skyscraper, 2-String Kite, W-Wing, Simple
// Coloring, AIC, ALS-XZ, Forcing Chain). Lives on its own route so the
// default Play page stays focused on classic-style solving.
const LAB_TIERS = [
  {
    key: 'advanced',
    name: 'Single-digit chains',
    range: [11, 13],
    color: '#FF9F0A',
  },
  {
    key: 'expert',
    name: 'Multi-digit logic',
    range: [14, 17],
    color: '#AF52DE',
  },
];

export default function AdvancedLabPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [difficulties, setDifficulties] = useState([]);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/difficulties`)
      .then(({ data }) => setDifficulties(data))
      .catch(() => toast.error('Failed to load Advanced Lab levels'));
  }, []);

  const isPremium = ['premium', 'premium_plus'].includes(user?.subscription_tier);

  const startGame = async (level) => {
    if (!isPremium) {
      toast.error('Advanced Lab levels require Premium');
      navigate('/subscription');
      return;
    }
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/puzzles/generate`,
        { difficulty_level: level },
        { withCredentials: true }
      );
      navigate(`/play/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate puzzle');
    } finally {
      setGenerating(false);
    }
  };

  const labLevels = difficulties.filter((d) => d.id >= 11 && d.id <= 17);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-main)' }}>
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/play')}
          className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] mb-6"
          data-testid="back-to-play"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Classic Play
        </button>

        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#AF52DE]/15 text-[#AF52DE] text-xs font-mono mb-3">
            <FlaskConical className="w-3.5 h-3.5" /> Advanced Lab
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[var(--text-primary)] mb-2">
            Beyond Classic Sudoku
          </h1>
          <p className="text-[var(--text-secondary)] max-w-2xl mx-auto">
            Levels 11–17 cover rare, quirky patterns most casual solvers will
            never need: Skyscraper, 2-String Kite, W-Wing, Simple Coloring, AIC,
            ALS-XZ, and Forcing Chain. They reward specific configurations more
            than fluent classical play.
          </p>
        </div>

        {!isPremium && (
          <Card className="bg-gradient-to-r from-[#AF52DE]/15 to-[#8B5A3C]/15 border-[#AF52DE]/30 mb-8">
            <CardContent className="p-5 text-center">
              <Crown className="w-8 h-8 text-[#AF52DE] mx-auto mb-2" />
              <p className="text-sm text-[var(--text-secondary)] mb-3">
                The Advanced Lab is a Premium-only space — every level here
                requires a paid subscription.
              </p>
              <Button
                onClick={() => navigate('/subscription')}
                className="bg-[#AF52DE] hover:bg-[#C77BFF] text-white"
                data-testid="lab-upgrade-cta"
              >
                Unlock Premium
              </Button>
            </CardContent>
          </Card>
        )}

        <div className="space-y-10">
          {LAB_TIERS.map((tier) => {
            const tierLevels = labLevels.filter(
              (d) => d.id >= tier.range[0] && d.id <= tier.range[1]
            );
            if (tierLevels.length === 0) return null;
            return (
              <div key={tier.key} data-testid={`lab-tier-${tier.key}`}>
                <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                  <h3
                    className="font-heading text-lg font-semibold flex items-center gap-2"
                    style={{ color: tier.color }}
                  >
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: tier.color }}
                    />
                    {tier.name}
                    <span className="font-mono text-xs text-[var(--text-disabled)] font-normal">
                      Levels {tier.range[0]}–{tier.range[1]}
                    </span>
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/tutorials/${tier.range[0]}`)}
                    style={{ borderColor: `${tier.color}40` }}
                    data-testid={`lab-learn-${tier.key}`}
                  >
                    <BookOpen className="w-4 h-4 mr-2" style={{ color: tier.color }} />
                    Learn techniques
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {tierLevels.map((diff) => (
                    <Card
                      key={diff.id}
                      className={`bg-[var(--background-surface)] border-[var(--border-subtle)] transition-all ${
                        isPremium ? 'cursor-pointer hover:border-[var(--border-strong)]' : 'opacity-70'
                      }`}
                      data-testid={`lab-card-${diff.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-8 h-8 rounded flex items-center justify-center"
                              style={{ backgroundColor: tier.color + '20' }}
                            >
                              <Target className="w-4 h-4" style={{ color: tier.color }} />
                            </div>
                            <span className="font-mono text-sm text-[var(--text-disabled)]">
                              Level {diff.id}
                            </span>
                          </div>
                          {!isPremium && <Lock className="w-4 h-4 text-[var(--text-disabled)]" />}
                        </div>
                        <h4 className="font-heading font-semibold text-[var(--text-primary)] mb-1">
                          {diff.name}
                        </h4>
                        <p className="text-sm text-[var(--text-secondary)] mb-3 line-clamp-2">
                          {diff.description}
                        </p>
                        <Button
                          onClick={() => startGame(diff.id)}
                          disabled={!isPremium || generating}
                          size="sm"
                          className="w-full bg-[#8B5A3C] hover:bg-[#A46944] text-white"
                          data-testid={`lab-start-${diff.id}`}
                        >
                          {!isPremium ? (
                            <span className="flex items-center gap-1.5">
                              <Lock className="w-4 h-4" /> Premium only
                            </span>
                          ) : (
                            <span className="flex items-center gap-1.5">
                              <Play className="w-4 h-4" /> Start
                            </span>
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
